import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { CloseButton, FontText, SecondaryButton } from '../common';

interface ChapterType {
  id: number;
  number: number;
  title: string;
  is_premium: boolean;
}

interface ChapterListProps {
  isOpen: boolean;
  chapters: ChapterType[];
  currentChapter: number;
  bookTitle: string;
  bookSlug: string;
  onClose: () => void;
  onChapterSelect?: (chapterNumber: number) => void;
}

export const ChapterList: React.FC<ChapterListProps> = ({
  isOpen,
  chapters,
  currentChapter,
  bookTitle,
  bookSlug,
  onClose,
  onChapterSelect,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  if (!isOpen) return null;

  const styles: Record<string, React.CSSProperties> = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 20,
    },
    modal: {
      backgroundColor: theme.colors.surface,
      borderRadius: 12,
      maxWidth: 500,
      width: '100%',
      maxHeight: '80vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      border: `1px solid ${theme.colors.overlay}`,
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '20px 30px',
      borderBottom: `1px solid ${theme.colors.overlay}`,
      backgroundColor: theme.colors.background,
    },
    title: {
      color: theme.colors.text,
      margin: 0,
    },
    chapterList: {
      flex: 1,
      overflowY: 'auto',
      padding: '10px 0',
    },
    chapterItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      padding: '15px 30px',
      backgroundColor: 'transparent',
      border: 'none',
      borderBottom: `1px solid ${theme.colors.overlay}`,
      color: theme.colors.textSecondary,
      cursor: 'pointer',
      textAlign: 'left',
      transition: 'background-color 0.2s',
      textDecoration: 'none',
    },
    activeChapter: {
      backgroundColor: theme.colors.overlay,
      color: theme.colors.accent,
    },
    chapterInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      flex: 1,
    },
    chapterNumber: {
      color: theme.colors.text,
    },
    chapterTitle: {
      color: theme.colors.textSecondary,
      opacity: 0.7,
    },
    premiumBadge: {
      marginLeft: 10,
    },
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <FontText h2 style={styles.title}>
            {bookTitle}
          </FontText>
          <CloseButton onClose={onClose} />
        </div>

        <div style={styles.chapterList}>
          {chapters.map(chapter => (
            <Link
              key={chapter.id}
              to={`/books/${bookSlug}/chapter/${chapter.number}`}
              style={{
                ...styles.chapterItem,
                ...(chapter.number === currentChapter
                  ? styles.activeChapter
                  : {}),
              }}
              onClick={() => onChapterSelect?.(chapter.number)}
              onMouseEnter={e => {
                if (chapter.number !== currentChapter) {
                  e.currentTarget.style.backgroundColor = theme.colors.overlay;
                }
              }}
              onMouseLeave={e => {
                if (chapter.number !== currentChapter) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <div style={styles.chapterInfo}>
                <FontText small style={styles.chapterNumber}>
                  {t('chapter_label', { number: chapter.number })}
                </FontText>
                <FontText tiny style={styles.chapterTitle}>
                  {chapter.title}
                </FontText>
              </div>
              {chapter.is_premium && (
                <FontText style={styles.premiumBadge}>🔒</FontText>
              )}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};
